import { CssBaseline, ThemeProvider } from "@mui/material";
import { ColorModeContext, useMode } from "./theme";
import {
  Route,
  Routes,
  useLocation,
  Navigate,
  useNavigate,
} from "react-router-dom";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Topbar from "./pages/global/Topbar";
import Sidebar from "./pages/global/Sidebar";
import Dashboard from "./pages/dashboard";
import { ToastContainer } from "react-toastify";
import SubscriptionPage from "./pages/SubscriptionPage";
import ReportPage from "./pages/ReportPage";
import PrivateRoute from "./components/PrivateRoute";
import LoginPage from "./pages/auth/login";
import SignUpPage from "./pages/auth/SingUpPage";
import ForgotPasswordPage from "./pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "./pages/auth/ResetPasswordPage";
import CompoundPage from "./pages/CompoundPage";
import CMTManagementPage from "./pages/CMTManagementPage";
import FinancialPage from "./pages/FinancialPage";
import AnnouncementPage from "./pages/AnnouncementPage";
import SystemSettingPage from "./pages/SystemSettingPage";
import IssueManagementPage from "./pages/IssueManagementPage";
import TranslationsPage from "./pages/TranslationsPage";
import ActivityLogPage from "./pages/ActivityLogPage";

import UserPage from "./pages/user";
import { NotificationProvider } from "./provider/NotificationProvider";

import { useEffect, useMemo, useState } from "react";
import { AuthProvider } from "./provider/AuthProvider";
import { ReauthWrapper } from "./components/ReauthWrapper";
import NotificationPage from "./pages/notification";
import SystemHealthPage from "./pages/SystemHealthPage";
import SecurityPage from "./pages/SecurityPage";
import BackupPage from "./pages/BackupPage";

import i18n from "./i18n";
import { useTranslation } from "react-i18next";
import { CacheProvider } from "@emotion/react";
import createCache from "@emotion/cache";
import rtlPlugin from "stylis-plugin-rtl";

function App() {
  const { i18n } = useTranslation();
  const direction = i18n.language === "ar" ? "rtl" : "ltr";

  const [theme, colorMode] = useMode(direction);
  const location = useLocation(); // Add this
  const cacheRtl = useMemo(
    () =>
      createCache({
        key: direction === "rtl" ? "muirtl" : "muiltr",
        stylisPlugins: direction === "rtl" ? [rtlPlugin] : [],
      }),
    [direction]
  );
  useEffect(() => {
    document.documentElement.lang = i18n.language;
    document.body.dir = direction;
  }, [i18n.language, direction]);

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = `
    .goog-te-banner-frame.skiptranslate,
    .goog-te-balloon-frame,
    .goog-logo-link,
    .goog-te-gadget,
    .skiptranslate {
      display: none !important;
      visibility: hidden !important;
      opacity: 0 !important;
      pointer-events: none !important;
    }
    body {
      top: 0 !important;
    }
  `;
    document.head.appendChild(style);
  }, []);

  const originalConsoleError = console.error;
  console.error = (...args) => {
    if (
      typeof args[0] === "string" &&
      args[0].includes("ResizeObserver loop completed")
    ) {
      return;
    }
    originalConsoleError(...args);
  };

  const authRoutes = [
    "/login",
    "/signup",
    "/remind-password",
    "/reset-password",
  ]; // ADD THIS
  const isAuthPage = authRoutes.some((route) =>
    location.pathname.startsWith(route)
  );
  const navigate = useNavigate();

  return (
    <ColorModeContext.Provider value={colorMode}>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <AuthProvider>
          <NotificationProvider>
            <CacheProvider value={cacheRtl}>
              <ThemeProvider theme={theme}>
                <CssBaseline />
                <ToastContainer
                  position="top-right"
                  autoClose={1000}
                  hideProgressBar={true}
                  newestOnTop={false}
                  closeOnClick
                  rtl={false}
                  pauseOnFocusLoss
                  draggable
                  pauseOnHover
                  theme="colored"
                />

                {isAuthPage ? (
                  // === Only Login Page ===
                  <Routes>
                    <Route path="/login" element={<LoginPage />} />
                    <Route path="/signup" element={<SignUpPage />} />
                    <Route
                      path="/remind-password"
                      element={<ForgotPasswordPage />}
                    />
                    <Route
                      path="/reset-password/:token"
                      element={<ResetPasswordPage />}
                    />
                    <Route
                      path="*"
                      element={<Navigate to="/login" replace />}
                    />
                  </Routes>
                ) : (
                  // === Main Dashboard Layout ===
                  <div className="app">
                    <Sidebar />
                    <main className="content">
                      <Topbar />
                      <div className="main-body">
                        <Routes>
                          <Route
                            path="/"
                            element={
                              <PrivateRoute>
                                <Dashboard />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/report"
                            element={
                              <PrivateRoute>
                                <ReportPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/compound"
                            element={
                              <PrivateRoute>
                                <CompoundPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/cmt"
                            element={
                              <PrivateRoute>
                                {" "}
                                <CMTManagementPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/user"
                            element={
                              <PrivateRoute>
                                <UserPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/financial"
                            element={
                              <PrivateRoute>
                                <FinancialPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/subscription"
                            element={
                              <PrivateRoute>
                                <SubscriptionPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/notification"
                            element={
                              <PrivateRoute>
                                <NotificationPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/setting"
                            element={
                              <PrivateRoute>
                                <SystemSettingPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/issue"
                            element={
                              <PrivateRoute>
                                <IssueManagementPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/logs"
                            element={
                              <PrivateRoute>
                                <ActivityLogPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/health"
                            element={
                              <PrivateRoute>
                                <SystemHealthPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/security"
                            element={
                              <PrivateRoute>
                                <SecurityPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/backup"
                            element={
                              <PrivateRoute>
                                <BackupPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="/translations"
                            element={
                              <PrivateRoute>
                                <TranslationsPage />
                              </PrivateRoute>
                            }
                          />
                          <Route
                            path="*"
                            element={<Navigate to="/" replace />}
                          />
                        </Routes>
                      </div>
                    </main>
                  </div>
                )}
              </ThemeProvider>
            </CacheProvider>
          </NotificationProvider>
        </AuthProvider>
      </LocalizationProvider>
    </ColorModeContext.Provider>
  );
}

export default App;
